import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { memo, useState, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FixedSizeList } from "react-window";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown.js";
import CloseIcon from "@mui/icons-material/Close.js";
import { Tooltip, MenuList } from "@mui/material";
import { useField } from "formik";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { getHasError } from "../../utils/helpers/getHasError.js";
import { getPhoneMetadata, metadataByCountryCode } from "../../utils/helpers/phoneMetadata.js";
import { useFormHelpers } from "../../utils/hooks/useFormHelpers.js";
import { useSubmitCount } from "../../utils/hooks/useSubmitCount.js";
import { TextFieldLabel } from "../../utils/shared/TextFieldLabel.js";
import { combinedFields } from "../../utils/yup/main.js";
import { FieldHelperText } from "../field-helper-text/field-helper-text.js";
import { TextInput } from "../text-input/text-input.js";
import { Translate } from "../translate/translate.js";
import { Container, Button, Flag, Popover, SearchClearButton, TextField, CallingCode, MenuItem, MenuItemText } from "./phone-field.styles.js";
import "yup";
import "../../utils/fields/personalInformation.js";
import "../../../../../type/Appointment.js";
import "../masked-date-input/masked-date-input.js";
import "react-text-mask";
import "../../utils/entities/Form.js";
import "../../../ana-toolbox/ana-toolbox.js";
import "../../../../../constants/date/date.js";
import "date-fns/fp/format";
import "date-fns/isValid";
import "date-fns/parse";
import "date-fns";
import "../../utils/helpers/toBoolean.js";
import "../../utils/helpers/createFields.js";
import "../../utils/helpers/resetFields.js";
import "../../utils/yup/yup.js";
import "../../../../../utils/pipe.js";
import "date-fns/fp";
import "libphonenumber-js/max";
import "libphonenumber-js/mobile";
import "../../utils/helpers/predicates.js";
import "../../utils/yup/emailValidation.js";
import "../../utils/fields/contactDetails.js";
import "../../utils/helpers/featureFlags.js";
import "../../utils/helpers/getAgeFromBirthday.js";
import "../../utils/yup/buildValidationSchema.js";
import "../../utils/fields/miscellaneous.js";
import "../../utils/fields/insurance.js";
import "../../utils/fields/allergiesAndMedications.js";
import "../../../../../type/Disease.js";
import "../../utils/config/medicaments.js";
import "../../utils/fields/wishesAndExpectations.js";
import "../../utils/fields/consents.js";
import "../../utils/fields/profileInterest.js";
import "../text-input/text-input.styles.js";
import "@emotion/react";
import "../../utils/shared/TextField.js";
const removeCountryCode = (countryCode, phoneNumber) => phoneNumber.replace(`+${countryCode}`, "").trim().replace("+", "");
const PhoneFieldBase = memo(
  ({
    value,
    error,
    name,
    language = "de",
    helperText,
    flagSource,
    required = false,
    label,
    onChange,
    onBlur,
    ...props
  }) => {
    const { t, i18n } = useTranslation();
    const [anchorPosition, setAnchorPosition] = useState(null);
    const [menuWidth, setMenuWidth] = useState("auto");
    const [searchTerm, setSearchTerm] = useState("");
    const inputRef = useRef(null);
    const countriesForCurrentLanguage = useMemo(() => getPhoneMetadata(language), [language]);
    const countriesFiltered = useMemo(
      () => countriesForCurrentLanguage.filter(
        (country) => country.name[language].toLowerCase().includes(searchTerm.toLowerCase())
      ),
      [countriesForCurrentLanguage, language, searchTerm]
    );
    const [currentCountryCode, setCurrentCountryCode] = useState(() => {
      try {
        const { country } = parsePhoneNumberFromString(value);
        return country || "de";
      } catch {
        return "de";
      }
    });
    const open = Boolean(anchorPosition);
    const currentCountry = useMemo(() => {
      return metadataByCountryCode[currentCountryCode.toUpperCase()];
    }, [currentCountryCode]);
    const displayValue = useMemo(
      () => value ? removeCountryCode(currentCountry == null ? void 0 : currentCountry.callingCode, value) : void 0,
      [value, currentCountry == null ? void 0 : currentCountry.callingCode]
    );
    const handleClick = (event) => {
      var _a;
      const parentElement = event.currentTarget.parentElement;
      if (parentElement) {
        setAnchorPosition({
          top: parentElement.offsetTop + 48,
          left: parentElement.offsetLeft
        });
        setMenuWidth(((_a = event.currentTarget.parentElement) == null ? void 0 : _a.clientWidth) || "auto");
      }
    };
    const handleSearchTermChange = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setSearchTerm(event.target.value);
    };
    const handleClearSearch = () => setSearchTerm("");
    const handleCountryClick = (country) => () => {
      setCurrentCountryCode(country.toLowerCase());
      setAnchorPosition(null);
      handleClearSearch();
      onChange({
        target: {
          name,
          value: ""
        }
      });
    };
    const handleClose = () => {
      setAnchorPosition(null);
    };
    const handleBlur = (e) => {
      onBlur(e);
    };
    const flagLabel = useMemo(() => {
      var _a;
      const countryName = (_a = metadataByCountryCode[currentCountryCode.toUpperCase()]) == null ? void 0 : _a.name[i18n.language];
      return `${countryName || "Unknown"} flag`;
    }, [i18n.language, currentCountryCode]);
    const renderCountry = ({ index, style }) => {
      const country = countriesFiltered[index];
      return /* @__PURE__ */ jsxs(
        MenuItem,
        {
          onClick: handleCountryClick(country.iso2code),
          selected: country.iso2code === currentCountry.iso2code,
          "data-testid": "menu-item",
          style,
          children: [
            /* @__PURE__ */ jsx(Flag, { image: flagSource(country.iso2code.toLowerCase()) }),
            /* @__PURE__ */ jsxs(MenuItemText, { children: [
              country.name[i18n.language],
              " +",
              country == null ? void 0 : country.callingCode
            ] })
          ]
        },
        country.code
      );
    };
    const MemoTextFieldLabel = memo(TextFieldLabel);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(Container, { children: [
        /* @__PURE__ */ jsxs(Button, { type: "button", onClick: handleClick, name: "country-flag", "aria-label": "Select country", children: [
          /* @__PURE__ */ jsx(Flag, { image: flagSource(currentCountryCode), "aria-label": flagLabel }),
          /* @__PURE__ */ jsx(ArrowDropDownIcon, {})
        ] }),
        open && /* @__PURE__ */ jsxs(
          Popover,
          {
            disableAutoFocus: true,
            anchorPosition: anchorPosition ?? void 0,
            disablePortal: true,
            BackdropProps: {
              open,
              "data-testid": "countries-backdrop",
              style: { backgroundColor: "transparent" }
            },
            keepMounted: true,
            open,
            onClose: handleClose,
            anchorReference: "anchorPosition",
            PaperProps: {
              style: { width: menuWidth }
            },
            children: [
              /* @__PURE__ */ jsx(
                TextInput,
                {
                  name: "countriesSearchTerm",
                  label: t("countries_search"),
                  value: searchTerm,
                  onChange: handleSearchTermChange,
                  endAdornment: searchTerm.length > 0 ? /* @__PURE__ */ jsx(Tooltip, { title: t("clear").toString(), children: /* @__PURE__ */ jsx(SearchClearButton, { name: "clearSearch", onClick: handleClearSearch, children: /* @__PURE__ */ jsx(CloseIcon, {}) }) }) : void 0
                }
              ),
              /* @__PURE__ */ jsx(MenuList, { children: /* @__PURE__ */ jsx(FixedSizeList, { height: 248, itemCount: countriesFiltered.length, itemSize: 48, width: 300, children: renderCountry }) })
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          TextField,
          {
            label: /* @__PURE__ */ jsx(MemoTextFieldLabel, { required, children: /* @__PURE__ */ jsx(Translate, { children: label }) }),
            InputProps: {
              startAdornment: /* @__PURE__ */ jsxs(CallingCode, { children: [
                "+",
                currentCountry == null ? void 0 : currentCountry.callingCode
              ] }),
              inputRef
            },
            InputLabelProps: {
              id: `${name}-label`
            },
            inputProps: {
              "aria-labelledby": `${name}-label`
            },
            value: displayValue || "",
            fullWidth: true,
            error: Boolean(error),
            type: "tel",
            name,
            onBlur: handleBlur,
            onChange: (e) => {
              onChange({
                target: {
                  name,
                  value: `+${currentCountry.callingCode}${removeCountryCode(currentCountryCode, e.target.value)}`
                }
              });
            },
            onPaste: (event) => {
              event.preventDefault();
              const clipboardData = event.clipboardData;
              const pastedData = clipboardData.getData("text");
              const onInvalidPastedData = () => {
                onChange({
                  target: {
                    name,
                    value: ""
                  }
                });
                setCurrentCountryCode("de");
              };
              if (typeof pastedData !== "string") {
                return onInvalidPastedData();
              }
              try {
                const { number, country } = parsePhoneNumberFromString(pastedData);
                if (number && country) {
                  onChange({
                    target: {
                      name,
                      value: number
                    }
                  });
                  setCurrentCountryCode(country.toLowerCase());
                }
              } catch {
                return onInvalidPastedData();
              }
            },
            ...props
          }
        )
      ] }),
      /* @__PURE__ */ jsx(FieldHelperText, { helperText, error, name })
    ] });
  }
);
const PhoneField = (props) => {
  const submitCount = useSubmitCount();
  const formHelpers = useFormHelpers();
  const [field, meta] = useField(props.name);
  const hasError = useMemo(() => getHasError(meta, submitCount ?? 1), [meta, submitCount]);
  const fieldSettings = useMemo(
    () => combinedFields[props.name],
    [props.name]
  );
  const handleChange = useCallback(
    (e) => {
      var _a, _b;
      (_b = (_a = fieldSettings == null ? void 0 : fieldSettings.hooks) == null ? void 0 : _a.beforeChange) == null ? void 0 : _b.call(_a, e.target.value, formHelpers, e);
      field.onChange(e);
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  return /* @__PURE__ */ jsx(PhoneFieldBase, { error: hasError ? meta.error : void 0, ...props, ...field, onChange: handleChange });
};
export {
  PhoneField
};
